'use strict';

// Renders the project location map(s) for the projects-grid block.
//
// Each `.projects-grid-map` container carries its data on data-attributes
// (project list as JSON + the API key), so this component is self-contained
// and supports multiple maps on a single page. The Google Maps API is loaded
// once and shared between every map instance.
//
// Interaction model:
//   - hover a marker  -> lightweight tooltip (title + short description)
//   - click/tap       -> navigate straight to the project page

const MAPS_CALLBACK = '__ecpProjectsMapReady';
let apiPromise = null;

// Load the Google Maps JS API a single time, resolving once it's ready.
const loadMapsApi = (apiKey) => {
    if (window.google && window.google.maps && window.google.maps.marker) {
        return Promise.resolve();
    }

    if (apiPromise) return apiPromise;

    apiPromise = new Promise((resolve, reject) => {
        window[MAPS_CALLBACK] = () => resolve();

        const script = document.createElement('script');
        script.src = `https://maps.googleapis.com/maps/api/js?key=${encodeURIComponent(apiKey)}&v=weekly&libraries=marker&callback=${MAPS_CALLBACK}`;
        script.async = true;
        script.onerror = reject;
        document.head.appendChild(script);
    });

    return apiPromise;
};

const PIN_SVG =
    '<svg viewBox="0 0 42 61" fill="none" xmlns="http://www.w3.org/2000/svg" aria-hidden="true"><path d="M20.9991 0C9.42006 0 0 9.49724 0 21.1697C0 32.4055 19.0528 58.6563 19.8641 59.7679L20.6212 60.8065C20.7098 60.9286 20.8507 61 20.9991 61C21.1497 61 21.2898 60.9286 21.3793 60.8065L22.1359 59.7679C22.9476 58.6563 42 32.4055 42 21.1697C42 9.49724 32.5784 0 20.9991 0ZM20.5 14C24.6364 14 28 17.3637 28 21.5C28 25.6341 24.6363 29 20.5 29C16.366 29 13 25.6341 13 21.5C13 17.3637 16.3659 14 20.5 14Z" fill="currentColor"/></svg>';

// Build the marker pin (a fresh DOM node per marker) with its own hover
// tooltip nested inside, so the tooltip tracks the marker and we can flip it
// above/below the pin depending on the room available in the map viewport.
const createPin = (project) => {
    const wrapper = document.createElement('div');
    wrapper.className = 'projects-grid-map__pin';

    const icon = document.createElement('div');
    icon.className = 'projects-grid-map__pin-icon';
    icon.innerHTML = PIN_SVG;
    wrapper.appendChild(icon);

    const tooltip = document.createElement('div');
    tooltip.className = 'projects-grid-map__tooltip';

    const title = document.createElement('p');
    title.className = 'projects-grid-map__tooltip-title';
    title.textContent = project.title;
    tooltip.appendChild(title);

    if (project.excerpt) {
        const text = document.createElement('p');
        text.className = 'projects-grid-map__tooltip-text';
        text.textContent = project.excerpt;
        tooltip.appendChild(text);
    }

    wrapper.appendChild(tooltip);

    return wrapper;
};

const initMap = async (container) => {
    const canvas = container.querySelector('[data-map-canvas]');
    const apiKey = container.dataset.apiKey;

    if (!canvas || !apiKey) return;

    let projects = [];
    try {
        projects = JSON.parse(container.dataset.projects || '[]');
    } catch (error) {
        return;
    }

    if (!projects.length) return;

    await loadMapsApi(apiKey);

    const { Map } = google.maps;
    const { AdvancedMarkerElement } = google.maps.marker;

    // A single-project map (e.g. the Project Info block) centres tightly on
    // its one location; the multi-project grid keeps a regional overview.
    const isSingle = projects.length === 1;

    const map = new Map(canvas, {
        center: isSingle
            ? { lat: projects[0].lat, lng: projects[0].lng }
            : { lat: 51.88326310325132, lng: -1.5573317138329266 },
        zoom: isSingle ? 13 : 11,
        // NOTE: when a mapId is set, JSON map styles applied in code are
        // ignored by Google. Style this map (e.g. hide POIs) against this
        // Map ID in the Google Cloud Console > Map Management.
        mapId: 'project_list_id',
        mapTypeControl: false,
        gestureHandling: 'cooperative',
        disableDefaultUI: true,
    });

    projects.forEach((project) => {
        const marker = new AdvancedMarkerElement({
            map,
            position: { lat: project.lat, lng: project.lng },
            title: project.title,
            content: createPin(project),
            gmpClickable: true,
        });

        const pin = marker.content;
        const tooltip = pin.querySelector('.projects-grid-map__tooltip');
        pin.style.cursor = 'pointer';

        pin.addEventListener('mouseenter', () => {
            // Flip the tooltip below the pin when there isn't enough room
            // above it inside the map viewport (e.g. markers near the top).
            const canvasRect = canvas.getBoundingClientRect();
            const pinRect = pin.getBoundingClientRect();
            const spaceAbove = pinRect.top - canvasRect.top;
            const flipBelow = spaceAbove < tooltip.offsetHeight + 16;

            pin.classList.toggle('projects-grid-map__pin--below', flipBelow);
            pin.classList.add('is-tooltip-visible');
            marker.zIndex = 1000; // bring the hovered pin (and tooltip) forward
        });

        pin.addEventListener('mouseleave', () => {
            pin.classList.remove('is-tooltip-visible');
            marker.zIndex = null;
        });

        marker.addListener('click', () => {
            if (project.url) {
                window.location.href = project.url;
            }
        });
    });
};

const initProjectsMap = () => {
    const maps = document.querySelectorAll('.projects-grid-map');
    maps.forEach((container) => {
        initMap(container);
    });
};

export default initProjectsMap;
